import { useState, useEffect, useRef, useMemo, useCallback, useContext } from "react"
import { Doughnut } from "react-chartjs-2"
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js"
import API from "../../../utils/API"
import Sweetalert from "../../../utils/Sweetalert"
import { SPINNER_DELAY_MS } from "../constants"
import UserContext from "../../../UserContext"
import { openResourcePreviewModal } from "../../Diffusion/ResourcePreviewModal"
import ButtonCollapse from "../../ButtonCollapse"
import "../AiTargetingStats.css"

ChartJS.register(ArcElement, Tooltip, Legend)

const TARGETED_PALETTE = [
  "#28a745", "#20c997", "#17a2b8", "#007bff", "#6f42c1",
  "#fd7e14", "#e83e8c", "#38d9a9", "#0dcaf0", "#8b5cf6",
]

const DEFAULT_PALETTE = ["#adb5bd", "#868e96", "#6c757d", "#ced4da", "#495057"]

const GENDER_LABELS = { M: "Hommes", F: "Femmes" }

const AD_TYPE_CONFIG = {
  1: { label: "Vidéo", icon: "fas fa-video" },
  2: { label: "Image", icon: "fas fa-image" },
  3: { label: "PDF", icon: "fas fa-file-pdf" },
}

const IMPRESSION_STATUS = {
  strong: { label: "Performant", className: "badge-success", icon: "fas fa-check-circle" },
  balanced: { label: "Correct", className: "badge-info", icon: "fas fa-thumbs-up" },
  low: { label: "À améliorer", className: "badge-warning", icon: "fas fa-exclamation-triangle" },
  very_low: { label: "Insuffisant", className: "badge-danger", icon: "fas fa-arrow-down" },
}

const TARGETED_ADVICE = {
  low: "Cette publicité ciblée reçoit peu d'impressions. Vérifiez que ses critères de ciblage correspondent à l'audience réelle détectée sur votre totem.",
  very_low: "Cette publicité est très rarement affichée. Vérifiez que ses critères de ciblage correspondent à l'audience réelle détectée sur votre totem ou envisagez d'élargir ses critères de ciblage (genre, tranches d'âge).",
}

function computeImpressionStatus(percent) {
  if (percent >= 20) return "strong"
  if (percent >= 10) return "balanced"
  if (percent >= 5) return "low"
  return "very_low"
}

function formatTargetingLabel(targeting) {
  if (!targeting || targeting.isDefault) return "Par défaut"

  const parts = []
  if (targeting.gender) parts.push(GENDER_LABELS[targeting.gender] || targeting.gender)
  if (targeting.ageRange?.length > 0) parts.push(targeting.ageRange.join(", "))

  return parts.join(" · ") || "Ciblée"
}

function processAds(rawAds) {
  const targeted = []
  const defaults = []

  for (const ad of rawAds) {
    ; (ad.targeting?.isDefault ? defaults : targeted).push(ad)
  }

  targeted.sort((a, b) => (b.impressions ?? 0) - (a.impressions ?? 0))
  defaults.sort((a, b) => (b.impressions ?? 0) - (a.impressions ?? 0))

  const totalImpressions = rawAds.reduce((sum, ad) => sum + (ad.impressions ?? 0), 0)

  const enrich = (ad, idx, isDefault) => {
    const palette = isDefault ? DEFAULT_PALETTE : TARGETED_PALETTE
    const impressions = ad.impressions ?? 0
    const percent = totalImpressions > 0 ? (impressions / totalImpressions) * 100 : 0
    const status = isDefault ? null : computeImpressionStatus(percent)
    const advice = status ? (TARGETED_ADVICE[status] ?? null) : null

    return { ...ad, color: palette[idx % palette.length], percent, isDefault, status, advice }
  }

  const targetedImpressions = targeted.reduce((sum, ad) => sum + (ad.impressions ?? 0), 0)
  const defaultImpressions = defaults.reduce((sum, ad) => sum + (ad.impressions ?? 0), 0)

  return {
    orderedAds: [
      ...targeted.map((ad, i) => enrich(ad, i, false)),
      ...defaults.map((ad, i) => enrich(ad, i, true)),
    ],
    totalImpressions,
    targetedCount: targeted.length,
    defaultCount: defaults.length,
    targetedPercent: totalImpressions > 0 ? (targetedImpressions / totalImpressions) * 100 : 0,
    defaultPercent: totalImpressions > 0 ? (defaultImpressions / totalImpressions) * 100 : 0,
  }
}

const ARC_LABEL_MIN_PERCENT = 5

const arcLabelsPlugin = {
  id: "doughnutArcLabels",
  afterDraw(chart) {
    const { ctx } = chart
    const dataset = chart.data.datasets[0]
    const meta = chart.getDatasetMeta(0)
    const total = dataset.data.reduce((sum, v) => sum + v, 0)
    if (total === 0) return

    ctx.save()
    ctx.textAlign = "center"
    ctx.textBaseline = "middle"

    meta.data.forEach((arc, i) => {
      const pct = (dataset.data[i] / total) * 100
      if (pct < ARC_LABEL_MIN_PERCENT) return

      const { startAngle, endAngle, innerRadius, outerRadius } = arc.getProps([
        "startAngle", "endAngle", "innerRadius", "outerRadius",
      ])
      const midAngle = (startAngle + endAngle) / 2
      const midRadius = (innerRadius + outerRadius) / 2
      const x = arc.x + Math.cos(midAngle) * midRadius
      const y = arc.y + Math.sin(midAngle) * midRadius

      ctx.font = "bold 11px sans-serif"
      ctx.fillStyle = "#fff"
      ctx.fillText(`${pct.toFixed(0)}%`, x, y)
    })

    ctx.restore()
  },
}

const centerTextPlugin = {
  id: "targetedDoughnutCenter",
  beforeDraw(chart) {
    const { ctx, chartArea } = chart
    if (!chartArea) return

    const total = chart.data.datasets[0].data.reduce((sum, v) => sum + v, 0)
    if (total === 0) return

    const cx = (chartArea.left + chartArea.right) / 2
    const cy = (chartArea.top + chartArea.bottom) / 2

    ctx.save()
    ctx.textAlign = "center"
    ctx.textBaseline = "middle"

    ctx.font = "bold 22px sans-serif"
    ctx.fillStyle = "#343a40"
    ctx.fillText(total.toLocaleString("fr-FR"), cx, cy - 10)

    ctx.font = "12px sans-serif"
    ctx.fillStyle = "#6c757d"
    ctx.fillText("impressions", cx, cy + 12)

    ctx.restore()
  },
}

const Spinner = () => <i className="fas fa-2x fa-sync-alt fa-spin"></i>

function ImpressionsDoughnut({ orderedAds, totalImpressions }) {
  const chartData = useMemo(
    () => ({
      labels: orderedAds.map((a) => a.adName),
      datasets: [
        {
          data: orderedAds.map((a) => a.impressions ?? 0),
          backgroundColor: orderedAds.map((a) => a.color),
          borderWidth: 2,
          borderColor: "#fff",
          hoverOffset: 8,
        },
      ],
    }),
    [orderedAds]
  )

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: "65%",
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (item) => {
              const pct = totalImpressions > 0 ? ((item.raw / totalImpressions) * 100).toFixed(1) : "0"
              return ` ${item.label}: ${item.raw.toLocaleString("fr-FR")} (${pct}%)`
            },
          },
        },
      },
    }),
    [totalImpressions]
  )

  if (totalImpressions === 0) {
    return (
      <div className="ai-stats-empty" style={{ height: 240 }}>
        <div className="ai-stats-empty__inner">
          <p className="ai-stats-empty__text">Aucune impression enregistrée</p>
        </div>
      </div>
    )
  }

  return (
    <div style={{ height: 240 }}>
      <Doughnut data={chartData} options={options} plugins={[centerTextPlugin, arcLabelsPlugin]} />
    </div>
  )
}

function GroupLegend({ targetedCount, defaultCount, targetedPercent, defaultPercent }) {
  return (
    <div className="ai-stats-doughnut__legend">
      <span className="ai-stats-doughnut__legend-item">
        <span className="ai-stats-doughnut__legend-dot" style={{ background: TARGETED_PALETTE[0] }}></span>
        Ciblées ({targetedCount})
        <span className="ai-stats-doughnut__legend-pct">{targetedPercent.toFixed(1)}%</span>
      </span>
      <span className="ai-stats-doughnut__legend-item">
        <span className="ai-stats-doughnut__legend-dot" style={{ background: DEFAULT_PALETTE[0] }}></span>
        Par défaut ({defaultCount})
        <span className="ai-stats-doughnut__legend-pct">{defaultPercent.toFixed(1)}%</span>
      </span>
    </div>
  )
}

function AdCard({ ad, onPreview }) {
  const typeConfig = AD_TYPE_CONFIG[ad.adTypeId]
  const statusConfig = IMPRESSION_STATUS[ad.status]

  return (
    <div className="ai-stats-doughnut__card" style={{ borderLeftColor: ad.color }}>
      <div className="ai-stats-doughnut__card-header">
        <div className="ai-stats-doughnut__card-info">
          <span className="ai-stats-doughnut__card-dot" style={{ background: ad.color }}></span>
          <div className="ai-stats-doughnut__card-name-wrap">
            <span className="ai-stats-doughnut__card-name">{ad.adName}</span>
            <span className="ai-stats-doughnut__card-meta">
              {typeConfig && (
                <span className="badge badge-light text-muted mr-1">
                  <i className={`${typeConfig.icon} mr-1`}></i>
                  {typeConfig.label}
                </span>
              )}
              <span className="text-muted">{formatTargetingLabel(ad.targeting)}</span>
            </span>
          </div>
        </div>
        {ad.adTypeId && (
          <button
            className="btn btn-default btn-sm"
            title="Prévisualiser"
            onClick={() => onPreview(ad)}
          >
            <i className="far fa-eye"></i>
          </button>
        )}
      </div>

      <div className="ai-stats-doughnut__card-stats">
        <div className="ai-stats-doughnut__card-stats-row">
          <span className="ai-stats-doughnut__card-impressions">
            {(ad.impressions ?? 0).toLocaleString("fr-FR")}{" "}
            <small className="text-muted">impressions</small>
          </span>
          <span className="ai-stats-doughnut__card-percent">{ad.percent.toFixed(1)}%</span>
        </div>
        <div className="progress ai-stats-doughnut__card-progress">
          <div
            className="progress-bar"
            style={{ width: `${Math.min(ad.percent, 100)}%`, background: ad.color }}
          ></div>
        </div>
        {statusConfig && (
          <span className={`badge ${statusConfig.className} mt-3`}>
            <i className={`${statusConfig.icon} mr-1`}></i>
            {statusConfig.label}
          </span>
        )}
      </div>

      {ad.advice && (
        <div className="ai-stats-doughnut__card-advice">
          <i className="fas fa-lightbulb"></i>
          <span>{ad.advice}</span>
        </div>
      )}
    </div>
  )
}

export default function TargetedDefaultDoughnut({ queryParams }) {
  const userContext = useContext(UserContext)
  const [rawData, setRawData] = useState(null)
  const [showSpinner, setShowSpinner] = useState(true)
  const spinnerTimerRef = useRef(null)
  const hasPreviousData = useRef(false)

  const handlePreview = useCallback(
    (ad) => {
      openResourcePreviewModal(
        { id: ad.adId, name: ad.adName, adTypeId: ad.adTypeId, width: ad.adWidth, height: ad.adHeight, landscape: ad.adLandscape },
        userContext
      )
    },
    [userContext]
  )

  useEffect(() => {
    const controller = new AbortController()

    clearTimeout(spinnerTimerRef.current)

    if (!hasPreviousData.current) {
      setShowSpinner(true)
    } else {
      spinnerTimerRef.current = setTimeout(() => setShowSpinner(true), SPINNER_DELAY_MS)
    }

    API.GET(
      `/ai-targeting-stats/ad-performance?${queryParams}`,
      (response) => {
        clearTimeout(spinnerTimerRef.current)
        setRawData(response.data)
        setShowSpinner(false)
        hasPreviousData.current = true
      },
      () => {
        clearTimeout(spinnerTimerRef.current)
        Sweetalert.fireErrorToast("Impossible de charger les données publicitaires")
        setRawData(null)
        setShowSpinner(false)
      },
      { signal: controller.signal }
    )

    return () => {
      controller.abort()
      clearTimeout(spinnerTimerRef.current)
    }
  }, [queryParams])

  const { orderedAds, totalImpressions, targetedCount, defaultCount, targetedPercent, defaultPercent } = useMemo(
    () =>
      rawData?.length > 0
        ? processAds(rawData)
        : { orderedAds: [], totalImpressions: 0, targetedCount: 0, defaultCount: 0, targetedPercent: 0, defaultPercent: 0 },
    [rawData]
  )

  const hasData = orderedAds.length > 0

  const targetedAdsNeedingAttention = orderedAds.filter(
    (ad) => !ad.isDefault && (ad.status === "low" || ad.status === "very_low")
  ).length

  return (
    <div className="card card-success card-outline">
      <div className="card-header">
        <h3 className="card-title">
          <i className="fas fa-ad mr-2"></i>
          Performance des publicités
        </h3>
        <div className="card-tools">
          {hasData && targetedAdsNeedingAttention > 0 && (
            <span className="badge badge-warning mr-2">
              <i className="fas fa-exclamation-triangle mr-1"></i>
              {targetedAdsNeedingAttention} à optimiser
            </span>
          )}
          <ButtonCollapse />
        </div>
      </div>
      <div className="card-body">
        {showSpinner ? (
          <div className="ai-stats-spinner" style={{ height: 240 }}>
            <Spinner />
          </div>
        ) : !hasData ? (
          <div className="ai-stats-empty" style={{ height: 240 }}>
            <div className="ai-stats-empty__inner">
              <i className="fas fa-chart-pie ai-stats-empty__icon"></i>
              <p className="ai-stats-empty__text">Aucune donnée disponible pour cette période.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="ai-stats-perf-summary">
              <div className="ai-stats-perf-summary__chart">
                <ImpressionsDoughnut orderedAds={orderedAds} totalImpressions={totalImpressions} />
              </div>
              <div className="ai-stats-perf-summary__legend">
                <GroupLegend
                  targetedCount={targetedCount}
                  defaultCount={defaultCount}
                  targetedPercent={targetedPercent}
                  defaultPercent={defaultPercent}
                />
              </div>
            </div>

            <div className="ai-stats-doughnut__divider">
              <span>Détail par publicité</span>
            </div>

            <div className="row">
              {orderedAds.map((ad) => (
                <div key={ad.adId} className="col-md-6 mb-4">
                  <AdCard ad={ad} onPreview={handlePreview} />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
